import { FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from "@/shared/components";
import { cn } from "@/shared/utils";
import type { ChangeEvent, ClipboardEvent, DragEvent, HTMLInputTypeAttribute } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

interface InputFormFieldProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    label?: string;
    name: FieldPath<T>;
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    readOnly?: boolean;
    type?: HTMLInputTypeAttribute;
    min?: number;
    max?: number;
    maxLength?: number;
    onValueChange?: (value: number | string) => void;
    transformValueOnBlur?: (value: string, setValue: (value: string) => void) => void;
    optional?: boolean;
    disabled?: boolean;
    helpText?: string;
    autoFocus?: boolean;
    "data-testid"?: string;
}

export function FormFieldInput<T extends FieldValues>({
    form,
    label,
    name,
    placeholder,
    className,
    inputClassName,
    labelClassName,
    readOnly,
    type = "text",
    min,
    max,
    maxLength,
    onValueChange,
    transformValueOnBlur,
    optional,
    disabled,
    helpText,
    autoFocus,
    "data-testid": dataTestid,
}: Readonly<InputFormFieldProps<T>>) {
    const handleInsert = (e: ClipboardEvent<HTMLInputElement> | DragEvent<HTMLInputElement>) => {
        if (typeof maxLength !== "number") return;

        const insertedText = "clipboardData" in e ? e.clipboardData.getData("text") : e.dataTransfer.getData("text");

        const input = e.currentTarget;
        const current = input.value ?? "";
        const selStart = input.selectionStart ?? current.length;
        const selEnd = input.selectionEnd ?? current.length;
        const nextLength = current.length - (selEnd - selStart) + insertedText.length;

        if (nextLength > maxLength) {
            e.preventDefault();
            form.setError(name, {
                type: "maxLength",
                message: `Maximaal ${maxLength} tekens`,
            });
        }
    };

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);

                    // Clear manual maxLength paste error once user edits the value
                    const fieldError = form.getFieldState(name, form.formState).error;
                    if (fieldError?.type === "maxLength") {
                        form.clearErrors(name);
                    }

                    if (onValueChange) {
                        const value = type === "number" ? parseInt(e.target.value) || 0 : e.target.value;
                        onValueChange(value);
                    }
                };

                const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
                    transformValueOnBlur?.(e.target.value, (value) => {
                        field.onChange(value);
                    });

                    field.onBlur();
                };

                return (
                    <FormItem className={className}>
                        {label && (
                            <FormLabel optional={optional} className={labelClassName}>
                                {label}
                            </FormLabel>
                        )}
                        <FormControl>
                            {readOnly ? (
                                <span className="w-full truncate">{field.value || "Onbekend"}</span>
                            ) : (
                                <Input
                                    className={cn("w-full", inputClassName)}
                                    placeholder={placeholder}
                                    data-testid={dataTestid}
                                    type={type}
                                    min={min}
                                    max={max}
                                    maxLength={maxLength}
                                    onPaste={handleInsert}
                                    onDrop={handleInsert}
                                    {...field}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    disabled={disabled}
                                    autoFocus={autoFocus}
                                />
                            )}
                        </FormControl>
                        {helpText && <span className="text-content-subtle text-s leading-l">{helpText}</span>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
